import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Download, 
  Eye,
  Grid3x3,
  Loader2,
  Image as ImageIcon,
  Palette,
  Wand2,
  TrendingUp,
  FileImage,
  Layout
} from 'lucide-react';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { getAuthHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_SIZES = {
  SQUARE_1080: { name: 'Square Post', desc: '1080×1080 - Instagram, Facebook' },
  STORY_1080x1920: { name: 'Story', desc: '1080×1920 - Instagram Stories, WhatsApp' },
  POSTER_A4: { name: 'Poster', desc: '2480×3508 - Printable A4' }
};

const DigiBranding = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [generating, setGenerating] = useState(false);
  const [templates, setTemplates] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryTemplates, setCategoryTemplates] = useState([]);
  
  // Images tab state
  const [imageSearchTerm, setImageSearchTerm] = useState('');
  const [imageResults, setImageResults] = useState([]);
  const [imageSearching, setImageSearching] = useState(false);
  const [imageSource, setImageSource] = useState('combined'); // pexels, pixabay, combined
  const [imageType, setImageType] = useState('photo'); // photo, illustration, vector
  const [imageOrientation, setImageOrientation] = useState('all');
  const [imageColor, setImageColor] = useState('');
  const [curatedImages, setCuratedImages] = useState([]);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/branding/stats`, {
        headers: getAuthHeaders()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/branding/templates`);
      setTemplates(response.data.templates);
      toast.success(`Loaded ${response.data.totalTemplates} templates!`);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const generateTemplates = async () => {
    try {
      setGenerating(true);
      toast.info('Generating templates... This may take a minute.');
      
      const response = await axios.post(
        `${API}/branding/generate-templates`,
        {},
        { headers: getAuthHeaders() }
      );
      
      toast.success(
        `Generated ${response.data.totalTemplates} templates for ${response.data.categoriesCount} categories!`
      );
      
      await loadStats();
      await loadTemplates();
      setActiveTab('gallery');
    } catch (error) {
      console.error('Failed to generate templates:', error);
      toast.error('Failed to generate templates');
    } finally {
      setGenerating(false);
    }
  };

  const viewCategoryTemplates = async (category) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/branding/templates/${encodeURIComponent(category)}`);
      setCategoryTemplates(response.data.templates);
      setSelectedCategory(category);
      setActiveTab('preview');
    } catch (error) {
      console.error('Failed to load category templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };


  // Image search functions
  const searchStockImages = async () => {
    if (!imageSearchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    try {
      setImageSearching(true);
      const response = await axios.get(`${API}/images/search`, {
        params: {
          query: imageSearchTerm,
          source: imageSource,
          image_type: imageType,
          per_page: 30,
          orientation: imageOrientation !== 'all' ? imageOrientation : null,
          color: imageColor || null
        }
      });

      setImageResults(response.data.photos || []);
      toast.success(`Found ${response.data.photos?.length || 0} images!`);
    } catch (error) {
      console.error('Failed to search images:', error);
      toast.error('Failed to search images');
    } finally {
      setImageSearching(false);
    }
  };

  const loadCuratedImages = async () => {
    try {
      setImageSearching(true);
      const response = await axios.get(`${API}/images/curated`, {
        params: { per_page: 30 }
      });
      setCuratedImages(response.data.photos || []);
    } catch (error) {
      console.error('Failed to load curated images:', error);
    } finally {
      setImageSearching(false);
    }
  };

  // Load curated images when Images tab is opened
  useEffect(() => {
    if (activeTab === 'images' && curatedImages.length === 0) {
      loadCuratedImages();
    }
  }, [activeTab]);


  const categories = Object.keys(templates);
  const filteredCategories = searchTerm
    ? categories.filter(cat => cat.toLowerCase().includes(searchTerm.toLowerCase()))
    : categories;

  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
              <Palette className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Digi Branding</h1>
              <p className="text-gray-600">Auto-generate branded templates for all business categories</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('dashboard')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'dashboard'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Dashboard
          </button>
          <button
            onClick={() => {
              setActiveTab('gallery');
              if (Object.keys(templates).length === 0) loadTemplates();
            }}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'gallery'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Template Gallery
          </button>

          <button
            onClick={() => setActiveTab('images')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'images'
                ? 'text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Stock Images
          </button>

          {activeTab === 'preview' && (
            <button
              onClick={() => setActiveTab('preview')}
              className="px-4 py-2 font-medium text-purple-600 border-b-2 border-purple-600"
            >
              {selectedCategory}
            </button>
          )}
        </div>

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Grid3x3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <span className="text-sm text-gray-500">Total</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {stats?.lastGeneration?.totalTemplates || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Templates Generated</div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-sm text-gray-500">Categories</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {stats?.lastGeneration?.categoriesCount || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Business Types</div>
              </Card>

              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-500">Sessions</span>
                </div>
                <div className="text-3xl font-bold text-gray-900">
                  {stats?.totalGenerations || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">Generation Runs</div>
              </Card>
            </div>

            {/* Main Action Card */}
            <Card className="p-8 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200">
              <div className="max-w-3xl mx-auto text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center mx-auto mb-6">
                  <Wand2 className="w-10 h-10 text-white" />
                </div>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Generate Branded Templates
                </h2>
                
                <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
                  Automatically create professional marketing templates for all 186 business categories. 
                  Each category gets 3 template sizes: Square Post, Story, and Printable Poster.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <FileImage className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">Square Post</div>
                    <div className="text-sm text-gray-600">1080×1080px</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <ImageIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">Story</div>
                    <div className="text-sm text-gray-600">1080×1920px</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-purple-200">
                    <Layout className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <div className="font-semibold text-gray-900">Poster</div>
                    <div className="text-sm text-gray-600">2480×3508px</div>
                  </div>
                </div>

                <div className="mb-6">
                  <Button
                    onClick={() => window.location.href = '/digi-branding/editor'}
                    size="lg"
                    className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white px-8 py-6 text-lg"
                  >
                    <Palette className="w-5 h-5 mr-2" />
                    Open Professional Editor (Demo)
                  </Button>
                  <p className="text-sm text-gray-600 mt-2">
                    Try the core editor with 5 premade templates
                  </p>
                </div>

                <Button
                  onClick={generateTemplates}
                  disabled={generating}
                  size="lg"
                  className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-8 py-6 text-lg"
                >
                  {generating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating Templates...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5 mr-2" />
                      Generate All Templates
                    </>
                  )}
                </Button>

                <p className="text-sm text-gray-600 mt-4">
                  This will generate 558 templates (186 categories × 3 sizes)
                </p>
              </div>
            </Card>

            {/* Features */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                    <Palette className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Brand Customization</h3>
                    <p className="text-sm text-gray-600">
                      Each template supports auto-binding of your brand colors, fonts, and logo. 
                      Perfect for maintaining brand consistency.
                    </p>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <ImageIcon className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">High-Quality Images</h3>
                    <p className="text-sm text-gray-600">
                      Templates use Lorem Picsum placeholder images. Upgrade to Unsplash integration 
                      for professional stock photography.
                    </p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        )}

        {/* Gallery Tab */}
        {activeTab === 'gallery' && (
          <div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mr-3" />
                <span className="text-gray-600">Loading templates...</span>
              </div>
            ) : categories.length === 0 ? (
              <Card className="p-12 text-center">
                <Grid3x3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">No Templates Yet</h3>
                <p className="text-gray-600 mb-6">
                  Generate templates first to see them here
                </p>
                <Button
                  onClick={() => setActiveTab('dashboard')}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Go to Dashboard
                </Button>
              </Card>
            ) : (
              <>
                {/* Search */}
                <div className="mb-6">
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search categories..."
                    className="max-w-md"
                  />
                  <p className="text-sm text-gray-600 mt-2">
                    Showing {filteredCategories.length} of {categories.length} categories
                  </p>
                </div>

                {/* Category Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredCategories.map(category => {
                    const categoryTemplates = templates[category] || [];
                    const previewTemplate = categoryTemplates[0];
                    
                    return (
                      <Card
                        key={category}
                        className="overflow-hidden hover:shadow-lg transition-all cursor-pointer group"
                        onClick={() => viewCategoryTemplates(category)}
                      >
                        {/* Preview Image */}
                        <div className="aspect-video bg-gradient-to-br from-purple-100 to-blue-100 relative overflow-hidden">
                          {previewTemplate?.previewUrl && (
                            <img
                              src={previewTemplate.previewUrl}
                              alt={category}
                              className="w-full h-full object-cover"
                            />
                          )}
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all flex items-center justify-center">
                            <div className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-purple-600 px-4 py-2 rounded-full font-semibold">
                              <Eye className="w-4 h-4 inline mr-2" />
                              View Templates
                            </div>
                          </div>
                        </div>
                        
                        {/* Info */}
                        <div className="p-4">
                          <h3 className="font-semibold text-gray-900 mb-2">{category}</h3>
                          <div className="flex items-center justify-between text-sm text-gray-600">
                            <span>{categoryTemplates.length} templates</span>
                            <span className="text-purple-600 font-medium">→</span>
                          </div>
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}

        {/* Preview Tab */}
        {activeTab === 'preview' && selectedCategory && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedCategory}</h2>
                <p className="text-gray-600">{categoryTemplates.length} templates available</p>
              </div>
              <Button
                variant="outline"
                onClick={() => setActiveTab('gallery')}
              >
                ← Back to Gallery
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {categoryTemplates.map(template => (
                <Card key={template.id} className="overflow-hidden">
                  {/* Template Preview */}
                  <div className="bg-gray-100 p-8">
                    <div 
                      className="bg-white mx-auto shadow-lg"
                      style={{
                        width: '100%',
                        aspectRatio: `${template.width}/${template.height}`
                      }}
                    >
                      <img
                        src={template.previewUrl}
                        alt={template.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-semibold text-gray-900">{template.title}</h3>
                        <p className="text-sm text-gray-600">
                          {TEMPLATE_SIZES[template.size]?.desc || template.size}
                        </p>
                      </div>
                      <div className="text-xs text-gray-500">
                        {template.width}×{template.height}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        className="flex-1 bg-purple-600 hover:bg-purple-700"
                        onClick={() => {
                          toast.success('Template copied to clipboard!');
                          navigator.clipboard.writeText(JSON.stringify(template, null, 2));
                        }}
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Copy JSON
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          window.open(template.previewUrl, '_blank');
                        }}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        Full Size
                      </Button>
                    </div>

                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-xs text-gray-600">
                        <div className="mb-1">Blocks: {template.blocks.length}</div>
                        <div>Source: {template.meta.source}</div>
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Stock Images Tab */}
        {activeTab === 'images' && (
          <div>
            {/* Search Section */}
            <Card className="p-6 mb-6 bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Search Stock Images & Cliparts</h2>
              
              {/* Search Box */}
              <div className="flex gap-3 mb-4">
                <Input
                  value={imageSearchTerm}
                  onChange={(e) => setImageSearchTerm(e.target.value)}
                  placeholder="Search photos, illustrations, vectors..."
                  className="flex-1"
                  onKeyPress={(e) => e.key === 'Enter' && searchStockImages()}
                />
                <Button
                  onClick={searchStockImages}
                  disabled={imageSearching}
                  className="bg-purple-600 hover:bg-purple-700 px-6"
                >
                  {imageSearching ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Searching...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      Search
                    </>
                  )}
                </Button>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                {/* Source */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select
                    value={imageSource}
                    onChange={(e) => setImageSource(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="combined">Both (Pexels + Pixabay)</option>
                    <option value="pexels">Pexels Only</option>
                    <option value="pixabay">Pixabay Only</option>
                  </select>
                </div>

                {/* Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                  <select
                    value={imageType}
                    onChange={(e) => setImageType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="photo">Photos</option>
                    <option value="illustration">Illustrations</option>
                    <option value="vector">Vectors/Cliparts</option>
                  </select>
                </div>

                {/* Orientation */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Orientation</label>
                  <select
                    value={imageOrientation}
                    onChange={(e) => setImageOrientation(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="all">All</option>
                    <option value="landscape">Landscape</option>
                    <option value="portrait">Portrait</option>
                    <option value="square">Square</option>
                  </select>
                </div>

                {/* Color */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Color</label>
                  <select
                    value={imageColor}
                    onChange={(e) => setImageColor(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  >
                    <option value="">Any Color</option>
                    <option value="red">Red</option>
                    <option value="orange">Orange</option>
                    <option value="yellow">Yellow</option>
                    <option value="green">Green</option>
                    <option value="blue">Blue</option>
                    <option value="purple">Purple</option>
                    <option value="pink">Pink</option>
                    <option value="black">Black</option>
                    <option value="white">White</option>
                  </select>
                </div>
              </div>
            </Card>

            {/* Results */}
            {imageSearching ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-purple-600 mr-3" />
                <span className="text-gray-600">Searching images...</span>
              </div>
            ) : imageResults.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Search Results ({imageResults.length} images)
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setImageResults([])}
                  >
                    Clear Results
                  </Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {imageResults.map((img, idx) => (
                    <Card key={idx} className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all">
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        <img
                          src={img.thumb || img.url}
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              className="bg-white text-purple-600 hover:bg-gray-100"
                              onClick={() => {
                                navigator.clipboard.writeText(img.url);
                                toast.success('Image URL copied!');
                              }}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Copy URL
                            </Button>
                          </div>
                        </div>
                        {/* Source Badge */}
                        <div className={`absolute top-2 right-2 text-xs px-2 py-1 rounded-full font-semibold ${
                          img.source === 'pexels' 
                            ? 'bg-teal-500 text-white' 
                            : 'bg-green-500 text-white'
                        }`}>
                          {img.source}
                        </div>
                      </div>
                      <div className="p-2">
                        <div className="text-xs text-gray-600 truncate">{img.photographer}</div>
                        <div className="text-xs text-gray-400">{img.width}×{img.height}</div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ) : curatedImages.length > 0 ? (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Curated Photos from Pexels
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {curatedImages.map((img, idx) => (
                    <Card key={idx} className="overflow-hidden group cursor-pointer hover:shadow-lg transition-all">
                      <div className="aspect-square bg-gray-100 relative overflow-hidden">
                        <img
                          src={img.thumb || img.url}
                          alt={img.alt}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              size="sm"
                              className="bg-white text-purple-600 hover:bg-gray-100"
                              onClick={() => {
                                navigator.clipboard.writeText(img.url);
                                toast.success('Image URL copied!');
                              }}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Copy URL
                            </Button>
                          </div>
                        </div>
                      </div>
                      <div className="p-2">
                        <div className="text-xs text-gray-600 truncate">{img.photographer}</div>
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            ) : (
              <Card className="p-12 text-center">
                <ImageIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Search Stock Images</h3>
                <p className="text-gray-600">
                  Search millions of free photos, illustrations, and vectors from Pexels and Pixabay
                </p>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DigiBranding;
